import type { NavigationState } from '@react-navigation/native';
import { buildAppRootResetAction } from '../../navigation/rootNavigationAuth';
import {
  getEffectiveAudience,
  getGuestOnlyRouteNames,
  getRegistryEntry,
} from './screenRegistry';
import type { NavigationIntentResult, ScreenAudience } from './types';

export function isGuestWelcomeRoute(route_name: string | undefined): boolean {
  if (route_name == null) {
    return false;
  }
  return getGuestOnlyRouteNames().includes(route_name);
}

export function resolveEffectiveAudienceForRoute(route_name: string): ScreenAudience | null {
  const entry = getRegistryEntry(route_name);
  if (!entry) {
    return null;
  }
  return getEffectiveAudience(entry);
}

/**
 * Whether the current user may navigate to `route_name`.
 * Used by navigationService and useAuthNavigation intercept.
 */
export function resolveNavigationIntent(
  route_name: string,
  is_authenticated: boolean,
): NavigationIntentResult {
  const audience = resolveEffectiveAudienceForRoute(route_name);

  if (audience == null) {
    return { action: 'allow' };
  }

  if (is_authenticated) {
    if (audience === 'guest_only') {
      return { action: 'redirect', target: 'authenticated_home' };
    }
    return { action: 'allow' };
  }

  if (audience === 'authenticated_only') {
    return { action: 'redirect', target: 'guest_home' };
  }

  return { action: 'allow' };
}

export interface AuthGuardResult {
  shouldReset: boolean;
  resetAction: ReturnType<typeof buildAppRootResetAction> | null;
}

/** When root stack (Auth vs Main) mismatches session — legacy guard for nested root navigators. */
export function getAuthGuardReset(state: NavigationState | undefined, is_authenticated: boolean): AuthGuardResult {
  const root_route = state?.routes?.[state.index ?? 0]?.name;
  if (!root_route) {
    return { shouldReset: false, resetAction: null };
  }

  if (is_authenticated && root_route === 'Main' && isGuestWelcomeRoute(root_route)) {
    return { shouldReset: true, resetAction: buildAppRootResetAction(true) };
  }

  if (!is_authenticated && root_route === 'MainTabs') {
    return { shouldReset: true, resetAction: buildAppRootResetAction(false) };
  }

  return { shouldReset: false, resetAction: null };
}

export function handleAuthGuardStateChange(
  state: NavigationState | undefined,
  is_authenticated: boolean,
  dispatch: (action: ReturnType<typeof buildAppRootResetAction>) => void,
): void {
  const { shouldReset, resetAction } = getAuthGuardReset(state, is_authenticated);
  if (shouldReset && resetAction) {
    dispatch(resetAction);
  }
}
